import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getStoredSessionId } from "../../Utility/session";
import { useDispatch, useSelector } from "react-redux";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import { setLoginData } from "../../redux/Reducers/AuthReducer/authSplice";
import { useSnackbarContext } from "../SnackbarContext";
import PopupDesign from "./PopupDesigns";
import PopupWheel from "./PopupWheel";

const SETTLE_WINDOW_MS = 1000; // let near-simultaneous triggers collect before first pump
const DISPLAY_GAP_MS = 5000;   // gap between consecutive popups
const IDLE_MS = 90000;
const BIG_WIN_POLL_MS = 12000;

const STATE_CHECKS = [
  { trigger: "coins_expiring", endpoint: "POPUP_STATE_COINS_EXPIRING" },
  { trigger: "deposit_lapsed", endpoint: "POPUP_STATE_DEPOSIT_LAPSED" },
  { trigger: "free_asset_pending", endpoint: "POPUP_STATE_FREESPIN_PENDING" },
  { trigger: "redemption_pending", endpoint: "POPUP_STATE_REDEMPTION_PENDING" },
  { trigger: "withdrawal_complete", endpoint: "POPUP_STATE_WITHDRAWAL_COMPLETE" },
  { trigger: "tier_upgrade_close", endpoint: "POPUP_STATE_TIER_UPGRADE_CLOSE" },
  { trigger: "high_cash_no_wager", endpoint: "POPUP_STATE_HIGH_CASH_NO_WAGER" },
  // Crypto-specific one-shot checks (NowPayments on-chain states)
  { trigger: "deposit_confirming", endpoint: "POPUP_STATE_DEPOSIT_CONFIRMING" },
  { trigger: "deposit_confirmed", endpoint: "POPUP_STATE_DEPOSIT_CONFIRMED" },
  { trigger: "withdrawal_broadcasting", endpoint: "POPUP_STATE_WITHDRAWAL_BROADCASTING" },
];

const PopupManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const IsLoginData = useSelector((state) => state.auth.IsLoginData);
  const { showSuccessSnackbar } = useSnackbarContext();
  const userId = IsLoginData?.idUser;

  const [current, setCurrent] = useState(null);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [wheelPopupId, setWheelPopupId] = useState(null);

  const queueRef = useRef([]);
  const shownRef = useRef(new Set());
  const pumpTimerRef = useRef(null);
  const currentRef = useRef(null);
  const sessionFiredRef = useRef(false);
  const lowFiredRef = useRef(false);
  const streakFiredRef = useRef(false);
  const idleTimerRef = useRef(null);
  const idleFiredRef = useRef(false);
  const bigWinSeenRef = useRef(new Set());
  const exitCallbacksRef = useRef({});

  currentRef.current = current;

  const isGameActive = location.pathname === "/game" || location.pathname === "/game/index-old";

  // Use the user's real login session id (same one persisted at login) so popup
  // impressions can be traced back to an actual session later — fall back to a
  // throwaway tag only if no real session is stored yet.
  const sessionIdRef = useRef(getStoredSessionId() || `sess_${Date.now()}`);

  const track = useCallback(
    (popupId, status, trigger) => {
      if (!userId || !popupId) return;
      addData(endPoints.api.POPUP_TRACK, {
        userId,
        popupId,
        status,
        trigger: trigger || null,
        sessionId: sessionIdRef.current,
      }).catch(() => {});
    },
    [userId]
  );

  const refreshUser = useCallback(async () => {
    try {
      const url = `${endPoints.api.GET_BY_USERID}/${userId}`;
      const response = await getAllData(url);
      dispatch(setLoginData({ user: response?.data, isAuthenticated: true }));
    } catch (e) {
      console.log(e);
    }
  }, [userId, dispatch]);

  const showPopup = useCallback(
    (offer) => {
      setCurrent(offer);
      if (!shownRef.current.has(offer.id)) {
        shownRef.current.add(offer.id);
        track(offer.id, "shown", offer.triggerEvent);
      }
    },
    [track]
  );

  const pump = useCallback(() => {
    if (currentRef.current) return;
    const next = queueRef.current.shift();
    if (next) showPopup(next);
  }, [showPopup]);

  const enqueue = useCallback(
    (offer) => {
      if (!offer) return;
      if (queueRef.current.some((o) => o.id === offer.id)) return;
      if (currentRef.current?.id === offer.id) return;
      const index = queueRef.current.findIndex((o) => (o.priority ?? 100) > (offer.priority ?? 100));
      if (index < 0) queueRef.current.push(offer);
      else queueRef.current.splice(index, 0, offer);
      if (pumpTimerRef.current) clearTimeout(pumpTimerRef.current);
      pumpTimerRef.current = setTimeout(pump, SETTLE_WINDOW_MS);
    },
    [pump]
  );

  const advance = useCallback(() => {
    setCurrent(null);
    currentRef.current = null;
    if (pumpTimerRef.current) clearTimeout(pumpTimerRef.current);
    pumpTimerRef.current = setTimeout(pump, DISPLAY_GAP_MS);
  }, [pump]);

  const fire = useCallback(
    async (trigger, options = {}) => {
      if (!userId) {
        // No user to resolve offers for. Returning silently here would strand the
        // caller's deferred action — exit_intent defers logout onto these callbacks,
        // so swallowing them makes the Logout button do nothing at all.
        options.onResolveEmpty && options.onResolveEmpty();
        return;
      }
      try {
        const response = await addData(endPoints.api.POPUP_RESOLVE, {
          userId,
          trigger,
          sessionId: sessionIdRef.current,
        });
        const winner = response?.data?.data;
        if (!winner) {
          options.onResolveEmpty && options.onResolveEmpty();
          return;
        }
        if (options.interrupt) {
          // exit-intent style: interrupt whatever is on screen
          if (pumpTimerRef.current) clearTimeout(pumpTimerRef.current);
          if (currentRef.current) track(currentRef.current.id, "dismissed", currentRef.current.triggerEvent);
          exitCallbacksRef.current = { onDismiss: options.onDismiss };
          showPopup(winner);
        } else {
          enqueue(winner);
        }
      } catch (e) {
        // A failed resolve call must never block the underlying action (e.g. logout), but
        // silently treating it the same as "no offer" makes real failures undiagnosable —
        // log it so a broken resolve call is visible instead of looking like a missing offer.
        console.error("popup resolve failed for trigger", trigger, e);
        options.onResolveEmpty && options.onResolveEmpty();
      }
    },
    [userId, enqueue, showPopup, track]
  );

  const dismiss = useCallback(() => {
    const offer = currentRef.current;
    if (offer) track(offer.id, "dismissed", offer.triggerEvent);
    const onDismiss = exitCallbacksRef.current.onDismiss;
    exitCallbacksRef.current = {};
    advance();
    if (onDismiss) onDismiss(); // exit_intent: dismissing still logs the user out
  }, [advance, track]);

  const openInApp = useCallback((link) => {
    if (!link) return;
    // Same in-app navigation as the email-package deep link (BuyCoinsNew.js reads
    // packageId/segment via useSearchParams) — SPA route change, no full page reload.
    if (/^https?:\/\//.test(link)) window.open(link, "_blank");
    else navigate(link);
  }, [navigate]);

  const cta = useCallback(async () => {
    const offer = currentRef.current;
    if (!offer) return;
    exitCallbacksRef.current = {}; // CTA on exit-intent cancels the deferred logout
    track(offer.id, "clicked", offer.triggerEvent);

    const isBuyFlow =
      offer.rewardMode === "on_purchase" ||
      offer.rewardType === "BOOSTER" ||
      (offer.ctaLink || "").includes("buy-coins");

    if (offer.rewardMode && offer.rewardMode !== "none" && offer.rewardType) {
      try {
        const response = await addData(endPoints.api.POPUP_CLAIM, { userId, popupId: offer.id });
        const result = response?.data?.data;
        if (result === "open_wheel") {
          setWheelPopupId(offer.id);
          setWheelOpen(true);
          setCurrent(null);
          currentRef.current = null;
          return; // advance happens when the wheel closes
        }
        if (result === "credited" && !isBuyFlow) {
          showSuccessSnackbar("Reward credited to your account!");
          refreshUser();
        }
        // "pending" / buy flows: say nothing — the purchase flow finishes the story.
      } catch (e) {
        console.log(e);
      }
    }
    advance();
    openInApp(offer.ctaLink);
  }, [userId, advance, openInApp, refreshUser, showSuccessSnackbar, track]);

  // ---- session_start / dormant_return + one-shot state checks ----
  useEffect(() => {
    if (!userId || sessionFiredRef.current) return;
    sessionFiredRef.current = true;

    const lastLogin = IsLoginData?.lastDailyLogin || IsLoginData?.dailyReward?.lastClaimTime;
    let dormant = false;
    if (lastLogin) {
      const daysSince = (Date.now() - new Date(lastLogin).getTime()) / 86400000;
      dormant = daysSince >= 7;
    }
    fire(dormant ? "dormant_return" : "session_start");

    STATE_CHECKS.forEach(({ trigger, endpoint }) => {
      getAllData(`${endPoints.api[endpoint]}/${userId}`)
        .then((res) => {
          const value = res?.data;
          if (value === true || (value && typeof value === "object")) fire(trigger);
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ---- external dispatches: exit_intent, package_hesitation, post_purchase, payment_failed ----
  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail || {};
      const { trigger, onDismiss, onResolveEmpty } = detail;
      if (!trigger) return;
      // Signal to the dispatcher that a manager is mounted and has taken ownership
      // of its callbacks. dispatchEvent resolves silently when nothing is listening,
      // so without this the caller cannot tell "deferred" from "dropped on the floor".
      detail.handled = true;
      if (trigger === "exit_intent") {
        fire(trigger, { interrupt: true, onDismiss, onResolveEmpty });
      } else {
        fire(trigger);
      }
    };
    window.addEventListener("popup:trigger", handler);
    return () => window.removeEventListener("popup:trigger", handler);
  }, [fire]);

  // ---- balance_low: watches live wallet, fires once, re-arms above threshold ----
  useEffect(() => {
    if (!userId) return;
    const total = (IsLoginData?.account?.ubalance || 0) + (IsLoginData?.account?.bonus_balance || 0);
    if (total < 1 && !lowFiredRef.current) {
      lowFiredRef.current = true;
      fire("balance_low");
    } else if (total >= 1) {
      lowFiredRef.current = false;
    }
  }, [userId, IsLoginData?.account?.ubalance, IsLoginData?.account?.bonus_balance, fire]);

  // ---- streak_at_risk: watches the flag the daily-streak feature already computes ----
  useEffect(() => {
    if (!userId) return;
    const atRisk = IsLoginData?.dailyReward?.streakRisk === true;
    if (atRisk && !streakFiredRef.current) {
      streakFiredRef.current = true;
      fire("streak_at_risk");
    } else if (!atRisk) {
      streakFiredRef.current = false;
    }
  }, [userId, IsLoginData?.dailyReward?.streakRisk, fire]);

  // ---- store_reentry: 2nd shop visit in-session without purchasing ----
  useEffect(() => {
    if (!userId) return;
    if (location.pathname === "/buy-coins") {
      const visits = Number(sessionStorage.getItem("popup_shop_visits") || "0") + 1;
      sessionStorage.setItem("popup_shop_visits", String(visits));
      if (visits >= 2) fire("store_reentry");
    }
  }, [userId, location.pathname, fire]);

  // ---- idle_active: deliberate-action reset (click only), gated off games/hidden tab ----
  useEffect(() => {
    if (!userId) return;
    const resetIdleTimer = () => {
      idleFiredRef.current = false;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (isGameActive || document.visibilityState !== "visible") return;
      idleTimerRef.current = setTimeout(() => {
        if (!idleFiredRef.current) {
          idleFiredRef.current = true;
          fire("idle_active");
        }
      }, IDLE_MS);
    };
    const events = ["click"]; // deliberate action only — NOT mousemove/scroll
    events.forEach((ev) => window.addEventListener(ev, resetIdleTimer));
    document.addEventListener("visibilitychange", resetIdleTimer);
    resetIdleTimer();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetIdleTimer));
      document.removeEventListener("visibilitychange", resetIdleTimer);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [userId, location.pathname, isGameActive, fire]);

  // ---- big_win: the one legitimate poll, only while a game route is open ----
  useEffect(() => {
    if (!userId || !isGameActive) return;
    const id = setInterval(() => {
      getAllData(`${endPoints.api.POPUP_STATE_BIGWIN}/${userId}`)
        .then((res) => {
          const win = res?.data;
          if (win && win.txid && !bigWinSeenRef.current.has(win.txid)) {
            bigWinSeenRef.current.add(win.txid);
            fire("big_win");
          }
        })
        .catch(() => {});
    }, BIG_WIN_POLL_MS);
    return () => clearInterval(id);
  }, [userId, isGameActive, fire]);

  if (!userId) return null;

  const currentContent = current
    ? (() => {
        try {
          return current.content ? JSON.parse(current.content) : {};
        } catch (e) {
          return {};
        }
      })()
    : {};

  const isToast = current?.template === "toast";

  return (
    <>
      {current && (
        <div
          style={
            isToast
              ? { position: "fixed", bottom: 20, right: 20, zIndex: 1500 }
              : {
                  position: "fixed",
                  inset: 0,
                  zIndex: 1500,
                  background: "rgba(0,0,0,0.55)",
                  display: "flex",
                  alignItems: current.template === "sheet" ? "flex-end" : "center",
                  justifyContent: "center",
                  padding: current.template === "sheet" ? 0 : 16,
                }
          }
        >
          <PopupDesign
            design={current.template}
            ctaText={current.ctaText}
            content={currentContent}
            endsAt={current.activeTo}
            onClose={dismiss}
            onCta={cta}
          />
        </div>
      )}
      <PopupWheel
        open={wheelOpen}
        popupId={wheelPopupId}
        userId={userId}
        onClose={() => {
          setWheelOpen(false);
          advance();
        }}
        onCredited={(message) => {
          showSuccessSnackbar(message);
          refreshUser();
        }}
        onPackage={(result) => {
          openInApp(result?.ctaLink || "/buy-coins");
        }}
      />
    </>
  );
};

export default PopupManager;
